/**
 * Section 2 — Control Exceptions. One card per Section IV exception, simplified
 * to five fields (#90): (1) control objective, (2) auditor's exception, (3) the
 * service org's management response, (4) the user org's management response —
 * AI-proposed (≤3 sentences) and editable, one field for any conclusion — and
 * (5) a management Conclusion dropdown. No AI compensating/rationale blocks; the
 * AI's original response shows only after the user overrides it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  SpaceBetween,
  Box,
  Button,
  ColumnLayout,
  Badge,
  FormField,
  Modal,
  SegmentedControl,
  ExpandableSection,
  StatusIndicator,
  Alert,
  Spinner,
  Textarea,
} from "@cloudscape-design/components";
import type { ControlException, ExceptionConclusion } from "@/core/api/types";
import { CommentThread } from "@/core/ui/CommentThread";
import { exceptionVerdict } from "@/core/ui/decisionLabel";
import { PdfPageLink } from "@/core/ui/PdfPageLink";
import { SectionStatusBadge } from "@/core/ui/SectionStatusBadge";
import { SubcardHeader } from "@/core/ui/SubcardHeader";
import { formatET } from "@/core/ui/time";
import { useSavedFlash } from "@/core/ui/useSavedFlash";
import type { Focus } from "@/shell/ReportDetail";
import { AddExceptionModal } from "./AddExceptionModal";
import { deleteException, listExceptions, updateException } from "./api";

interface Props {
  reportId: string;
  reportStatus: "uploading" | "processing" | "ready" | "error" | "awaiting_upload";
  openCount?: number;
  focus?: Focus | null;
  sectionPage?: number | null;
  /** #88: tell ReportDetail to refetch its open-items so the section header
   *  count updates immediately after a manual add/delete/conclude. */
  onItemsChanged?: () => void;
}

// #90: button-style Conclusion. The mitigation-source distinction (service /
// user org) becomes the conclusion itself. "" = unconcluded (stays Open).
type Conclusion = ExceptionConclusion | "";

const CONCLUSION_OPTIONS = [
  { id: "mitigated_service_org", text: "Mitigated by Service Org" },
  { id: "mitigated_user_org", text: "Mitigated by User Org" },
  { id: "unmitigated", text: "Unmitigated" },
  { id: "na", text: "N/A" },
];

/** Mitigated by … / N/A resolve; Unmitigated and unconcluded keep it Open (#90). */
const RESOLVING: Conclusion[] = ["mitigated_service_org", "mitigated_user_org", "na"];

/** The conclusion AIP-4 effectively proposed from its compensating read (#99) —
 *  choosing a different one drops the AI's seeded justification, so a fresh
 *  rationale comment is required before the exception can be Done. */
const SOURCE_TO_CONCLUSION: Record<string, ExceptionConclusion> = {
  "service-org": "mitigated_service_org",
  "user-org": "mitigated_user_org",
  none: "unmitigated",
};

/** Seed the button from the stored conclusion, else from AIP-4's compensating read. */
function initialConclusion(exc: ControlException): Conclusion {
  const c = exc.conclusion;
  if (c === "mitigated_service_org" || c === "mitigated_user_org" || c === "unmitigated" || c === "na")
    return c;
  const src = exc.compensating_source;
  if (src === "service-org") return "mitigated_service_org";
  if (src === "user-org") return "mitigated_user_org";
  if (src === "none") return "unmitigated";
  return "";
}

function ExceptionCard({
  exc,
  reportId,
  onUpdated,
  focusTargetId,
}: {
  exc: ControlException;
  reportId: string;
  onUpdated: () => void;
  focusTargetId?: string | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isFocused = focusTargetId === exc.id;
  // #109: exception cards start collapsed by default — only a deep-linked card
  // auto-expands. They still collapse on Mark done / re-expand on Reopen.
  const [expanded, setExpanded] = useState(isFocused);
  useEffect(() => {
    if (isFocused && containerRef.current) {
      setExpanded(true);
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isFocused]);

  const ai = exc.ai_data;
  const assessment = ai?.assessment;
  const qualified = ai?.objective_qualified === true;
  const aiResponse = assessment?.user_org_response ?? "";

  // #91: the user-org response is now a comment thread (CommentThread below).
  const [conclusion, setConclusion] = useState<Conclusion>(initialConclusion(exc));
  const [marked, setMarked] = useState(!!exc.marked_done);
  const [busy, setBusy] = useState(false);
  const [savedAt, flashSaved] = useSavedFlash(); // #119: auto-dismisses after ~5s
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // #88: management can correct AIP-3's extracted fields. Display = override ?? AI.
  const dObjective = exc.user_control_objective ?? ai?.control_objective ?? "";
  const dExcText = exc.user_exception_text ?? ai?.exception_text ?? "";
  const dMgmt = exc.user_management_response ?? ai?.management_response ?? "";
  // #88: the card header reads "CC6.1 — <short description>" so it's
  // identifiable at a glance (AI-extracted and manually-added alike).
  const headerDesc = (dExcText || exc.description || "").trim();
  const headerTitle = headerDesc
    ? `${exc.control_id} — ${headerDesc.length > 60 ? `${headerDesc.slice(0, 60)}…` : headerDesc}`
    : exc.control_id;
  const [editing, setEditing] = useState(false);
  const [objective, setObjective] = useState(dObjective);
  const [excText, setExcText] = useState(dExcText);
  const [mgmtResp, setMgmtResp] = useState(dMgmt);
  const startEdit = () => {
    setObjective(dObjective);
    setExcText(dExcText);
    setMgmtResp(dMgmt);
    setEditing(true);
  };
  // #C8: leave edit mode without saving — restore the displayed values.
  const cancelEdit = () => {
    setObjective(dObjective);
    setExcText(dExcText);
    setMgmtResp(dMgmt);
    setConclusion(initialConclusion(exc));
    setEditing(false);
  };

  const resolving = RESOLVING.includes(conclusion);
  // #99: a Done exception is read-only — it shows only the comment thread + an
  // Edit button; Edit reveals the Conclusion + fields (no Save/Reopen clutter).
  const readOnly = marked && !editing;

  // #117: an exception can't be Done without a documented rationale. The AI's
  // seeded justification counts — unless the chosen conclusion overrides the
  // AI's proposal, in which case that seed is dropped (server-side) and the user
  // must write a fresh rationale this session.
  const [freshComment, setFreshComment] = useState(false);
  const aiProposed = SOURCE_TO_CONCLUSION[exc.compensating_source ?? ""] ?? "";
  const conclusionOverridesAi = !!conclusion && conclusion !== aiProposed;
  const needsRationale = resolving && conclusionOverridesAi && !freshComment;
  const hasRationale = conclusionOverridesAi
    ? freshComment
    : (exc.comment_count ?? 0) > 0 || freshComment;
  const canMarkDone = resolving && hasRationale;

  const removeException = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteException(exc.id);
      setConfirmDelete(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const persist = async (mark?: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await updateException(exc.id, {
        conclusion: conclusion || null,
        ...(editing
          ? {
              user_control_objective: objective.trim() || null,
              user_exception_text: excText.trim() || null,
              user_management_response: mgmtResp.trim() || null,
            }
          : {}),
        ...(mark !== undefined ? { mark_done: mark } : {}),
      });
      if (mark !== undefined) setMarked(mark);
      // #109: collapse on Mark done; re-expand on Reopen; Save leaves it as-is.
      if (mark === true) setExpanded(false);
      else if (mark === false) setExpanded(true);
      setEditing(false);
      flashSaved();
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  const save = () => persist();
  const markDone = () => persist(true);
  const reopen = () => persist(false);

  return (
    <div ref={containerRef} className={isFocused ? "focus-flash" : undefined}>
      <ExpandableSection
        variant="container"
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
        header={
          <SubcardHeader
            title={headerTitle}
            status={marked ? <Badge color="green">Done</Badge> : <Badge color="red">Open</Badge>}
            actions={
              <>
                {qualified ? (
                  <Badge color="red">Objective qualified</Badge>
                ) : (
                  <Badge color="blue">Exception (objective unqualified)</Badge>
                )}
                {/* #114: the conclusion verdict shows only once the card is Done. */}
                {marked && conclusion && <Badge color="blue">{exceptionVerdict(conclusion)}</Badge>}
                {ai?.page && <PdfPageLink reportId={reportId} page={ai.page} />}
                {/* #119: edit lives in the subcard body ("Edit" button) — no
                    duplicate pencil in the header. */}
                <Button
                  iconName="remove"
                  variant="inline-link"
                  ariaLabel={`Delete exception ${exc.control_id}`}
                  onClick={() => setConfirmDelete(true)}
                />
              </>
            }
          />
        }
      >
        <SpaceBetween size="s">
          {error && (
            <Alert type="error" header="Save failed">
              {error}
            </Alert>
          )}
          {(editing || dObjective) && (
            <Box>
              <Box variant="awsui-key-label">Control objective</Box>
              {editing ? (
                <Textarea value={objective} onChange={({ detail }) => setObjective(detail.value)} rows={2} />
              ) : (
                <Box>{dObjective}</Box>
              )}
            </Box>
          )}
          {(editing || dExcText) && (
            <Box>
              <Box variant="awsui-key-label">Auditor's exception</Box>
              {editing ? (
                <Textarea value={excText} onChange={({ detail }) => setExcText(detail.value)} rows={3} />
              ) : (
                <Box>{dExcText}</Box>
              )}
            </Box>
          )}
          {(editing || dMgmt) && (
            <Box>
              <Box variant="awsui-key-label">Service organization's management response</Box>
              {editing ? (
                <Textarea value={mgmtResp} onChange={({ detail }) => setMgmtResp(detail.value)} rows={3} />
              ) : (
                <Box>{dMgmt}</Box>
              )}
            </Box>
          )}
          {editing && (
            <Box variant="small" color="text-body-secondary">
              Editing the AI-extracted fields. Save to keep your corrections (the report's original text
              stays on file).
            </Box>
          )}

          <Box>
            <Box variant="awsui-key-label">User organization's management response</Box>
            {needsRationale && (
              <Box padding={{ bottom: "xs" }}>
                <Alert type="info" header="Document the rationale for this conclusion">
                  You changed the conclusion from the AI's proposal. Add a comment with the rationale,
                  then Mark done.
                </Alert>
              </Box>
            )}
            <CommentThread
              kind="exception"
              targetId={exc.id}
              // #99: once the conclusion overrides the AI's proposal, don't
              // re-seed the contradictory AI draft.
              aiSeed={conclusionOverridesAi ? undefined : aiResponse}
              onChange={onUpdated}
              onPosted={() => setFreshComment(true)}
              autoOpen={needsRationale}
              helpText={
                needsRationale ? "Explain why this conclusion is appropriate, then submit." : undefined
              }
            />
          </Box>

          {!readOnly && (
            <FormField
              label="Conclusion"
              description="Mitigated by … or N/A can be marked done; Unmitigated stays open for management to document."
            >
              <SegmentedControl
                selectedId={conclusion}
                onChange={({ detail }) => {
                  setConclusion(detail.selectedId as Conclusion);
                  setFreshComment(false); // a new conclusion needs its own rationale (#117)
                }}
                options={CONCLUSION_OPTIONS}
                label="Exception conclusion"
              />
            </FormField>
          )}

          <SpaceBetween direction="horizontal" size="xs">
            {readOnly ? (
              // #99: Done exception — a single Edit reveals the decision + fields.
              <Button iconName="edit" onClick={startEdit} disabled={busy}>
                Edit
              </Button>
            ) : (
              <>
                <Button
                  variant="primary"
                  onClick={save}
                  disabled={busy || (marked && needsRationale)}
                  loading={busy}
                >
                  Save
                </Button>
                {marked ? (
                  <Button onClick={reopen} disabled={busy}>
                    Reopen
                  </Button>
                ) : (
                  <Button iconName="check" onClick={markDone} disabled={busy || !canMarkDone}>
                    Mark done
                  </Button>
                )}
                {/* #C8: Edit on a Done exception can be backed out. */}
                {editing && (
                  <Button onClick={cancelEdit} disabled={busy}>
                    Cancel
                  </Button>
                )}
              </>
            )}
            {/* #117: explain why Mark done is unavailable (Button has no tooltip). */}
            {!readOnly && !marked && !canMarkDone && (
              <Box variant="small" color="text-status-inactive" padding={{ top: "xxs" }}>
                {!resolving
                  ? "Pick a resolving conclusion (Mitigated by … or N/A) to mark done."
                  : "Document the rationale in a comment to mark done."}
              </Box>
            )}
            {savedAt && !busy && (
              <StatusIndicator type="success">
                {marked ? "Done" : "Saved"} {formatET(new Date(savedAt))}
              </StatusIndicator>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </ExpandableSection>

      <Modal
        visible={confirmDelete}
        onDismiss={() => setConfirmDelete(false)}
        header="Delete exception"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setConfirmDelete(false)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void removeException()} disabled={busy} loading={busy}>
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Delete this control exception? This can't be undone.
      </Modal>
    </div>
  );
}

export function ControlsSection({
  reportId,
  reportStatus,
  openCount = 0,
  focus,
  sectionPage,
  onItemsChanged,
}: Props) {
  const [exceptions, setExceptions] = useState<ControlException[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setExceptions(await listExceptions(reportId));
      onItemsChanged?.(); // #88: refresh the report-level open count too
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [reportId, onItemsChanged]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Auto-poll while ingest hasn't reached AIP-3 yet (report.status not 'ready')
  // OR ingest is done but no exceptions found YET — could be the AIP-3 call
  // in flight after scope finished.
  useEffect(() => {
    if (reportStatus !== "uploading" && reportStatus !== "processing") return;
    const id = setInterval(() => void refresh(), 5_000);
    return () => clearInterval(id);
  }, [reportStatus, refresh]);

  const ingestRunning = reportStatus === "uploading" || reportStatus === "processing";
  const expandDefault = focus?.section === "controls";
  // #C9: section header shows the total exception count, e.g. "Control Exceptions (3)".
  const sectionHeader = `Control Exceptions (${exceptions.length})`;
  const badge = (
    <SectionStatusBadge processing={ingestRunning} openCount={openCount} />
  );
  const addButton = (
    <Box>
      <Button iconName="add-plus" onClick={() => setAddOpen(true)}>
        Add exception
      </Button>
    </Box>
  );
  const addModal = (
    <AddExceptionModal
      reportId={reportId}
      visible={addOpen}
      onDismiss={() => setAddOpen(false)}
      onAdded={refresh}
    />
  );

  if (loading && !exceptions.length && !ingestRunning)
    return (
      <ExpandableSection
        variant="container"
        defaultExpanded={expandDefault}
        headerText="Control Exceptions"
        headerActions={badge}
      >
        <Box textAlign="center" padding="l">
          <Spinner /> Loading exceptions…
        </Box>
      </ExpandableSection>
    );

  if (error)
    return (
      <ExpandableSection
        variant="container"
        defaultExpanded={expandDefault}
        headerText="Control Exceptions"
        headerActions={badge}
      >
        <Alert
          type="error"
          header="Failed to load exceptions"
          action={<Button onClick={refresh}>Retry</Button>}
        >
          {error}
        </Alert>
      </ExpandableSection>
    );

  if (ingestRunning && !exceptions.length)
    return (
      <ExpandableSection
        variant="container"
        defaultExpanded={expandDefault}
        headerText="Control Exceptions"
        headerActions={badge}
      >
        <Box textAlign="center" padding="l" color="text-body-secondary">
          <Spinner /> Ingest is still running. AIP-3 extracts Section IV exceptions after Scope finishes —
          this view auto-refreshes when ready.
        </Box>
      </ExpandableSection>
    );

  if (!exceptions.length)
    return (
      <ExpandableSection
        variant="container"
        defaultExpanded={expandDefault}
        headerText={sectionHeader}
        headerActions={badge}
      >
        <SpaceBetween size="s">
          {typeof sectionPage === "number" && (
            <Box>
              <PdfPageLink reportId={reportId} page={sectionPage} />
            </Box>
          )}
          <Box color="text-body-secondary">No control exceptions found. Add one if the AI missed it.</Box>
          {addButton}
        </SpaceBetween>
        {addModal}
      </ExpandableSection>
    );

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded={expandDefault}
      headerText={sectionHeader}
      headerActions={badge}
    >
      <SpaceBetween size="s">
        <ColumnLayout columns={1}>
          {exceptions.map((e) => (
            <ExceptionCard
              key={e.id}
              exc={e}
              reportId={reportId}
              onUpdated={refresh}
              focusTargetId={focus?.targetId ?? null}
            />
          ))}
        </ColumnLayout>
        {addButton}
      </SpaceBetween>
      {addModal}
    </ExpandableSection>
  );
}
